// Training Session Editor Page
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth-context';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  ArrowLeft,
  Save,
  Loader2,
  Calendar,
  Users,
  Euro,
  DollarSign,
  Sparkles,
  Copy,
  ExternalLink,
  Eye,
  RefreshCw,
  Linkedin,
  Facebook,
  Instagram,
  Twitter,
  CheckCircle,
  CreditCard,
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  Landmark,
  UserCheck,
  AlertCircle,
  Trash2
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const currencies = [
  { value: 'EUR', label: 'Euro (€)', symbol: '€' },
  { value: 'USD', label: 'Dollar ($)', symbol: '$' },
  { value: 'TND', label: 'Dinar Tunisien (TND)', symbol: 'TND' }
];

export default function TrainingSessionEditorPage() {
  const { workspaceId: urlWorkspaceId, sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // isNew is true when sessionId is 'new' OR when sessionId is undefined (direct /training/new route)
  const isNew = !sessionId || sessionId === 'new';

  // Get initial tab from URL params
  const initialTab = searchParams.get('tab') || 'info';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingSocial, setGeneratingSocial] = useState(null);
  const [workspaceId, setWorkspaceId] = useState(urlWorkspaceId);

  // Form data
  const [form, setForm] = useState({
    name: '',
    description: '',
    program_content: '',
    start_date: '',
    end_date: '',
    course_type_id: '',
    modules: [],
    max_candidates: 20,
    primary_currency: 'EUR',
    primary_price: 0,
    landing_page_html: '',
    custom_registration_url: '',
    ai_custom_prompt: '',
    status: 'draft'
  });

  // Computed prices
  const [prices, setPrices] = useState({ EUR: 0, USD: 0, TND: 0 });

  // Reference data
  const [sessionTypes, setSessionTypes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [rates, setRates] = useState({ USD: 1.10, TND: 3.30 });

  // Social posts
  const [socialPosts, setSocialPosts] = useState({
    linkedin: { fr: '', en: '' },
    facebook: { fr: '', en: '' },
    instagram: { fr: '', en: '' },
    twitter: { fr: '', en: '' }
  });

  // Registrations
  const [registrations, setRegistrations] = useState([]);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);
  const [generatingPaymentLink, setGeneratingPaymentLink] = useState(null);
  const [paymentLinkDialog, setPaymentLinkDialog] = useState(null);
  const [deletingRegistration, setDeletingRegistration] = useState(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(null);
  const [confirmingPayment, setConfirmingPayment] = useState(null);
  const [confirmPaymentDialog, setConfirmPaymentDialog] = useState(null);

  const [activeTab, setActiveTab] = useState(initialTab);
  const [previewHtml, setPreviewHtml] = useState(false);

  const token = localStorage.getItem('token');
  
  // Determine base path for navigation
  const basePath = urlWorkspaceId ? `/workspace/${workspaceId}/training` : '/training';
  // Le retour renvoie vers la Vitrine (gestion des sessions depuis /admin/vitrine)
  const returnPath = urlWorkspaceId ? `/workspace/${workspaceId}/training` : '/admin/vitrine';

  useEffect(() => {
    const init = async () => {
      let wsId = urlWorkspaceId || user?.current_workspace_id || localStorage.getItem('current_workspace_id');
      
      if (!wsId) {
        try {
          const response = await fetch(`${API_URL}/api/workspaces`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const workspaces = await response.json();
          if (workspaces?.length > 0) {
            wsId = workspaces[0].id;
            localStorage.setItem('current_workspace_id', wsId);
          }
        } catch (error) {
          console.error('Failed to fetch workspaces:', error);
        }
      }
      
      setWorkspaceId(wsId);
    };
    
    init();
  }, [urlWorkspaceId, user]);

  useEffect(() => {
    if (workspaceId) {
      loadReferenceData();
      if (!isNew) {
        loadSession();
      }
    }
  }, [sessionId, workspaceId]);

  useEffect(() => {
    // Calculate prices when primary price or currency changes
    calculatePrices();
  }, [form.primary_price, form.primary_currency, rates]);

  const loadReferenceData = async () => {
    try {
      // Load session types (namespace admin, pas de workspace)
      const typesRes = await fetch(
        `${API_URL}/api/training/session-types`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (typesRes.ok) setSessionTypes(await typesRes.json());

      // Load courses for modules selection
      const coursesRes = await fetch(
        `${API_URL}/api/workspaces/${workspaceId}/courses`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (coursesRes.ok) setCourses(await coursesRes.json());

      // Load currency rates (namespace admin, pas de workspace)
      const ratesRes = await fetch(
        `${API_URL}/api/training/currency-rates`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (ratesRes.ok) {
        const data = await ratesRes.json();
        setRates(data.rates);
      }
    } catch (error) {
      console.error('Failed to load reference data:', error);
    }
  };

  const loadSession = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/training/sessions/${sessionId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setForm({
          name: data.name || '',
          description: data.description || '',
          start_date: data.start_date || '',
          end_date: data.end_date || '',
          course_type_id: data.course_type_id || '',
          modules: data.modules || [],
          max_candidates: data.max_candidates || 20,
          primary_currency: data.primary_currency || 'EUR',
          primary_price: data.prices?.[data.primary_currency] || 0,
          landing_page_html: data.landing_page_html || '',
          status: data.status || 'draft'
        });
        setPrices(data.prices || { EUR: 0, USD: 0, TND: 0 });
      } else {
        toast.error('Session non trouvée');
        navigate(returnPath);
      }
    } catch (error) {
      console.error('Failed to load session:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const calculatePrices = () => {
    const basePrice = parseFloat(form.primary_price) || 0;
    const baseCurrency = form.primary_currency;

    let eurPrice = basePrice;
    if (baseCurrency === 'USD') {
      eurPrice = basePrice / rates.USD;
    } else if (baseCurrency === 'TND') {
      eurPrice = basePrice / rates.TND;
    }

    setPrices({
      EUR: Math.round(eurPrice * 100) / 100,
      USD: Math.round(eurPrice * rates.USD * 100) / 100,
      TND: Math.round(eurPrice * rates.TND * 100) / 100
    });
  };

  const handleSave = async () => {
    if (!form.name || !form.start_date || !form.end_date || !form.course_type_id) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        start_date: form.start_date,
        end_date: form.end_date,
        course_type_id: form.course_type_id,
        modules: form.modules,
        max_candidates: parseInt(form.max_candidates),
        primary_currency: form.primary_currency,
        primary_price: parseFloat(form.primary_price),
        landing_page_html: form.landing_page_html
      };

      // Sessions rattachées au dashboard admin (namespace admin, pas de workspace)
      const url = isNew
        ? `${API_URL}/api/training/sessions`
        : `${API_URL}/api/training/sessions/${sessionId}`;

      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(isNew ? 'Session créée' : 'Session mise à jour');
        if (isNew) {
          navigate(`/training/${data.id}`);
        }
      } else {
        throw new Error('Save failed');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const generateLandingPage = async (regenerate = false) => {
    if (isNew) {
      toast.error('Sauvegardez d\'abord la session');
      return;
    }

    setGenerating(true);
    try {
      const response = await fetch(
        `${API_URL}/api/training/sessions/${sessionId}/generate-page`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            regenerate,
            custom_registration_url: form.custom_registration_url || null,
            custom_prompt: form.ai_custom_prompt || null
          })
        }
      );

      const data = await response.json();
      
      if (response.ok) {
        setForm(prev => ({ ...prev, landing_page_html: data.html }));
        toast.success('Page générée avec succès !');
      } else {
        throw new Error(data.detail || 'Generation failed');
      }
    } catch (error) {
      console.error('Generate error:', error);
      toast.error(`Erreur: ${error.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const generateSocialPost = async (platform, includeEnglish = false) => {
    if (isNew) {
      toast.error('Sauvegardez d\'abord la session');
      return;
    }

    setGeneratingSocial(platform);
    try {
      const response = await fetch(
        `${API_URL}/api/training/sessions/${sessionId}/generate-social`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ platform, include_english: includeEnglish })
        }
      );

      const data = await response.json();
      
      if (response.ok) {
        setSocialPosts(prev => ({
          ...prev,
          [platform]: { fr: data.content_fr, en: data.content_en || '' }
        }));
        toast.success('Post généré !');
      } else {
        throw new Error(data.detail || 'Generation failed');
      }
    } catch (error) {
      console.error('Generate social error:', error);
      toast.error(`Erreur: ${error.message}`);
    } finally {
      setGeneratingSocial(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié !');
  };

  const toggleModule = (courseId) => {
    setForm(prev => ({
      ...prev,
      modules: prev.modules.includes(courseId)
        ? prev.modules.filter(id => id !== courseId)
        : [...prev.modules, courseId]
    }));
  };

  // Load registrations for this session
  const loadRegistrations = async () => {
    if (isNew || !sessionId) return;
    
    setLoadingRegistrations(true);
    try {
      const response = await fetch(
        `${API_URL}/api/training/registrations?workspace_id=${workspaceId}&session_id=${sessionId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setRegistrations(data);
      }
    } catch (error) {
      console.error('Load registrations error:', error);
    } finally {
      setLoadingRegistrations(false);
    }
  };

  // Generate payment link for a registration
  const generatePaymentLink = async (registrationId, paymentMethod) => {
    setGeneratingPaymentLink(registrationId);
    try {
      const response = await fetch(
        `${API_URL}/api/training/registrations/${registrationId}/payment-link?workspace_id=${workspaceId}&payment_method=${paymentMethod}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();
      
      if (response.ok) {
        setPaymentLinkDialog(data);
        toast.success('Lien de paiement généré !');
        // Reload registrations to update status
        loadRegistrations();
      } else {
        throw new Error(data.detail || 'Erreur lors de la génération');
      }
    } catch (error) {
      console.error('Generate payment link error:', error);
      toast.error(`Erreur: ${error.message}`);
    } finally {
      setGeneratingPaymentLink(null);
    }
  };

  // Delete a registration
  const deleteRegistration = async (registrationId) => {
    setDeletingRegistration(registrationId);
    try {
      const response = await fetch(
        `${API_URL}/api/training/registrations/${registrationId}?workspace_id=${workspaceId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success('Inscription supprimée');
        // Reload registrations
        await loadRegistrations();
        setDeleteConfirmDialog(null);
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Erreur de suppression');
      }
    } catch (error) {
      console.error('Delete registration error:', error);
      toast.error(`Erreur: ${error.message}`);
    } finally {
      setDeletingRegistration(null);
    }
  };

  // Confirm payment manually (for bank transfers)
  const confirmPayment = async (registrationId) => {
    setConfirmingPayment(registrationId);
    try {
      const response = await fetch(
        `${API_URL}/api/training/registrations/${registrationId}/confirm-payment?workspace_id=${workspaceId}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success('Paiement confirmé ! Email de confirmation envoyé.');
        await loadRegistrations();
        setConfirmPaymentDialog(null);
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Erreur de confirmation');
      }
    } catch (error) {
      console.error('Confirm payment error:', error);
      toast.error(`Erreur: ${error.message}`);
    } finally {
      setConfirmingPayment(null);
    }
  };

  // Load registrations when switching to inscriptions tab
  useEffect(() => {
    if (activeTab === 'inscriptions' && !isNew) {
      loadRegistrations();
    }
  }, [activeTab, sessionId]);

  const getPaymentStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Payé</Badge>;
      case 'pending':
        return <Badge variant="outline" className="text-orange-600 border-orange-300"><Clock className="h-3 w-3 mr-1" />En attente</Badge>;
      default:
        return <Badge variant="outline"><AlertCircle className="h-3 w-3 mr-1" />Non payé</Badge>;
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(returnPath)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">
                {isNew ? 'Nouvelle session' : 'Modifier la session'}
              </h1>
              {!isNew && (
                <Badge variant={form.status === 'published' ? 'default' : 'secondary'}>
                  {form.status === 'draft' ? 'Brouillon' : form.status === 'published' ? 'Publiée' : form.status}
                </Badge>
              )}
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Enregistrer
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="info">Informations</TabsTrigger>
            <TabsTrigger value="pricing">Tarification</TabsTrigger>
            <TabsTrigger value="page">Page d'inscription</TabsTrigger>
            <TabsTrigger value="inscriptions" disabled={isNew}>
              <Users className="h-4 w-4 mr-1" />
              Inscriptions {registrations.length > 0 && `(${registrations.length})`}
            </TabsTrigger>
            <TabsTrigger value="promotion">Promotion</TabsTrigger>
          </TabsList>

          {/* Tab 1: Informations */}
          <TabsContent value="info" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Informations générales</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label>Nom de la session *</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Ex: Formation Docker Avancée - Avril 2026"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={form.description}
                      onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Décrivez brièvement cette session..."
                      rows={3}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date de début *</Label>
                    <Input
                      type="date"
                      value={form.start_date}
                      onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date de fin *</Label>
                    <Input
                      type="date"
                      value={form.end_date}
                      onChange={(e) => setForm(prev => ({ ...prev, end_date: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type de cours *</Label>
                    <Select
                      value={form.course_type_id}
                      onValueChange={(value) => setForm(prev => ({ ...prev, course_type_id: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un type" />
                      </SelectTrigger>
                      <SelectContent>
                        {sessionTypes.map(type => (
                          <SelectItem key={type.id} value={type.id}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Nombre max de candidats</Label>
                    <Input
                      type="number"
                      min="1"
                      value={form.max_candidates}
                      onChange={(e) => setForm(prev => ({ ...prev, max_candidates: e.target.value }))}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Modules Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Modules de formation</CardTitle>
                <CardDescription>Sélectionnez les cours inclus dans cette session</CardDescription>
              </CardHeader>
              <CardContent>
                {courses.length === 0 ? (
                  <p className="text-muted-foreground text-sm">Aucun cours disponible</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {courses.map(course => (
                      <div
                        key={course.id}
                        onClick={() => toggleModule(course.id)}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          form.modules.includes(course.id)
                            ? 'border-purple-500 bg-purple-500/10'
                            : 'border-border hover:border-purple-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {form.modules.includes(course.id) && (
                            <CheckCircle className="h-4 w-4 text-purple-500" />
                          )}
                          <span className="text-sm font-medium truncate">{course.title}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Program Content Editor */}
            <Card>
              <CardHeader>
                <CardTitle>Programme de la session</CardTitle>
                <CardDescription>Définissez le programme détaillé qui sera affiché sur la page d'inscription</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Contenu du programme</Label>
                  <Textarea
                    value={form.program_content}
                    onChange={(e) => setForm(prev => ({ ...prev, program_content: e.target.value }))}
                    placeholder={`Exemple de format:

JOUR 1 - Introduction
• Module 1: Concepts fondamentaux
• Module 2: Installation et configuration
• Exercices pratiques

JOUR 2 - Approfondissement
• Module 3: Fonctionnalités avancées
• Module 4: Cas d'usage réels
• TP: Projet pratique

JOUR 3 - Certification
• Révision générale
• Examen de certification
• Remise des attestations`}
                    rows={12}
                    className="font-mono text-sm"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Ce programme sera affiché sur la page d'inscription publique et inclus dans la génération IA de la landing page.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 2: Pricing */}
          <TabsContent value="pricing" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Tarification</CardTitle>
                <CardDescription>Définissez le prix principal et les conversions automatiques</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Devise principale</Label>
                    <Select
                      value={form.primary_currency}
                      onValueChange={(value) => setForm(prev => ({ ...prev, primary_currency: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map(c => (
                          <SelectItem key={c.value} value={c.value}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Prix ({form.primary_currency})</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.primary_price}
                      onChange={(e) => setForm(prev => ({ ...prev, primary_price: e.target.value }))}
                    />
                  </div>
                </div>

                {/* Price Preview */}
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm font-medium mb-3">Aperçu des prix dans toutes les devises:</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div className={`p-3 rounded-lg ${form.primary_currency === 'EUR' ? 'bg-purple-500/20 border-2 border-purple-500' : 'bg-background'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Euro className="h-4 w-4" />
                        <span className="text-sm">Euro</span>
                        {form.primary_currency === 'EUR' && <Badge className="text-xs">Principal</Badge>}
                      </div>
                      <p className="text-2xl font-bold">{prices.EUR} €</p>
                    </div>
                    <div className={`p-3 rounded-lg ${form.primary_currency === 'USD' ? 'bg-purple-500/20 border-2 border-purple-500' : 'bg-background'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <DollarSign className="h-4 w-4" />
                        <span className="text-sm">Dollar</span>
                        {form.primary_currency === 'USD' && <Badge className="text-xs">Principal</Badge>}
                      </div>
                      <p className="text-2xl font-bold">${prices.USD}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${form.primary_currency === 'TND' ? 'bg-purple-500/20 border-2 border-purple-500' : 'bg-background'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium">TND</span>
                        {form.primary_currency === 'TND' && <Badge className="text-xs">Principal</Badge>}
                      </div>
                      <p className="text-2xl font-bold">{prices.TND} TND</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 3: Landing Page */}
          <TabsContent value="page" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Page d'inscription</CardTitle>
                    <CardDescription>Générez et personnalisez votre page d'inscription</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => generateLandingPage(false)}
                      disabled={generating || isNew}
                    >
                      {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                      Générer avec IA
                    </Button>
                    {form.landing_page_html && (
                      <Button
                        variant="outline"
                        onClick={() => generateLandingPage(true)}
                        disabled={generating}
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Régénérer
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* URL d'inscription personnalisée */}
                <div className="space-y-2 p-4 border rounded-lg bg-muted/30">
                  <div className="flex items-center gap-2">
                    <ExternalLink className="h-4 w-4 text-primary" />
                    <Label className="font-medium">URL du formulaire d'inscription</Label>
                  </div>
                  <Input
                    value={form.custom_registration_url}
                    onChange={(e) => setForm(prev => ({ ...prev, custom_registration_url: e.target.value }))}
                    placeholder="https://votre-site.com/inscription ou laissez vide pour l'URL par défaut"
                  />
                  <p className="text-xs text-muted-foreground">
                    URL vers laquelle le bouton "S'inscrire" redirigera. Si vide, utilise: <code className="bg-muted px-1 rounded">/sessions/{form.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'slug'}/inscription</code>
                  </p>
                </div>

                {/* Prompt personnalisé pour l'IA */}
                <div className="space-y-2 p-4 border rounded-lg bg-muted/30">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <Label className="font-medium">Instructions personnalisées pour l'IA (optionnel)</Label>
                  </div>
                  <Textarea
                    value={form.ai_custom_prompt}
                    onChange={(e) => setForm(prev => ({ ...prev, ai_custom_prompt: e.target.value }))}
                    placeholder={`Exemples d'instructions:
- Utiliser un ton plus corporate et professionnel
- Mettre l'accent sur la certification à la fin
- Ajouter une section "Pré-requis" avec: connaissance de Linux, bases de scripting
- Couleurs principales: bleu foncé (#1a365d) et orange (#ed8936)
- Mentionner que c'est en partenariat avec [Entreprise]`}
                    rows={5}
                  />
                  <p className="text-xs text-muted-foreground">
                    Ajoutez des instructions spécifiques pour personnaliser le contenu généré par l'IA (ton, couleurs, sections spéciales, etc.)
                  </p>
                </div>

                {!isNew && form.status === 'published' && (
                  <div className="flex items-center gap-2 p-3 bg-green-500/10 rounded-lg">
                    <ExternalLink className="h-4 w-4 text-green-600" />
                    <span className="text-sm">URL publique par défaut: </span>
                    <code className="text-sm bg-muted px-2 py-1 rounded">
                      {window.location.origin}/sessions/{form.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}/inscription
                    </code>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(`${window.location.origin}/sessions/${form.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}/inscription`)}>
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Contenu HTML généré</Label>
                    <Button variant="ghost" size="sm" onClick={() => setPreviewHtml(true)}>
                      <Eye className="h-4 w-4 mr-1" />
                      Prévisualiser
                    </Button>
                  </div>
                  <Textarea
                    value={form.landing_page_html}
                    onChange={(e) => setForm(prev => ({ ...prev, landing_page_html: e.target.value }))}
                    placeholder="Le contenu HTML sera généré automatiquement par l'IA ou vous pouvez le modifier manuellement..."
                    rows={15}
                    className="font-mono text-xs"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab 4: Inscriptions */}
          <TabsContent value="inscriptions" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <UserCheck className="h-5 w-5" />
                      Liste des inscrits
                    </CardTitle>
                    <CardDescription>
                      {registrations.length} inscription{registrations.length > 1 ? 's' : ''} • 
                      {registrations.filter(r => r.payment_status === 'paid').length} payée{registrations.filter(r => r.payment_status === 'paid').length > 1 ? 's' : ''}
                    </CardDescription>
                  </div>
                  <Button variant="outline" onClick={loadRegistrations} disabled={loadingRegistrations}>
                    {loadingRegistrations ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingRegistrations ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : registrations.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium">Aucune inscription</h3>
                    <p className="text-muted-foreground">Les inscriptions apparaîtront ici</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Candidat</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Montant</TableHead>
                        <TableHead>Mode paiement</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {registrations.map((reg) => (
                        <TableRow key={reg.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{reg.full_name}</div>
                              <div className="text-xs text-muted-foreground flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {reg.country || 'Non spécifié'}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="text-sm flex items-center gap-1">
                                <Mail className="h-3 w-3 text-muted-foreground" />
                                {reg.email}
                              </div>
                              {reg.phone && (
                                <div className="text-sm flex items-center gap-1">
                                  <Phone className="h-3 w-3 text-muted-foreground" />
                                  {reg.phone}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">
                              {reg.amount} {reg.selected_currency}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={reg.payment_method === 'bank_transfer' ? 'border-orange-500 text-orange-600' : 'border-blue-500 text-blue-600'}>
                              {reg.payment_method === 'bank_transfer' ? (
                                <><Landmark className="h-3 w-3 mr-1" /> Virement</>
                              ) : (
                                <><CreditCard className="h-3 w-3 mr-1" /> Carte</>
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {getPaymentStatusBadge(reg.payment_status)}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm text-muted-foreground">
                              {reg.created_at ? new Date(reg.created_at).toLocaleDateString('fr-FR') : '-'}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            {reg.payment_status !== 'paid' ? (
                              <div className="flex gap-2 justify-end">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => generatePaymentLink(reg.id, 'stripe')}
                                  disabled={generatingPaymentLink === reg.id}
                                  title="Générer lien Stripe"
                                >
                                  {generatingPaymentLink === reg.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <CreditCard className="h-4 w-4" />
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => generatePaymentLink(reg.id, 'bank_transfer')}
                                  disabled={generatingPaymentLink === reg.id}
                                  title="Infos virement"
                                >
                                  <Landmark className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="default"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => setConfirmPaymentDialog(reg)}
                                  disabled={confirmingPayment === reg.id}
                                  title="Confirmer paiement"
                                >
                                  {confirmingPayment === reg.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <CheckCircle className="h-4 w-4" />
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => setDeleteConfirmDialog(reg)}
                                  disabled={deletingRegistration === reg.id}
                                  title="Supprimer inscription"
                                >
                                  {deletingRegistration === reg.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                </Button>
                              </div>
                            ) : (
                              <Badge className="bg-green-500">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Payé
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Summary Card */}
            {registrations.length > 0 && (
              <Card>
                <CardContent className="pt-6">
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div>
                      <div className="text-3xl font-bold">{registrations.length}</div>
                      <div className="text-sm text-muted-foreground">Total inscrits</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-green-600">
                        {registrations.filter(r => r.payment_status === 'paid').length}
                      </div>
                      <div className="text-sm text-muted-foreground">Payés</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-orange-600">
                        {registrations.filter(r => r.payment_status === 'pending').length}
                      </div>
                      <div className="text-sm text-muted-foreground">En attente</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold">
                        {registrations.filter(r => r.payment_status === 'paid').reduce((sum, r) => sum + (r.amount || 0), 0).toFixed(0)} €
                      </div>
                      <div className="text-sm text-muted-foreground">Revenus</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Tab 5: Promotion */}
          <TabsContent value="promotion" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Posts pour réseaux sociaux</CardTitle>
                <CardDescription>Générez du contenu marketing pour promouvoir votre session</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* LinkedIn */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Linkedin className="h-5 w-5 text-blue-600" />
                      <span className="font-medium">LinkedIn</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => generateSocialPost('linkedin', true)}
                      disabled={generatingSocial === 'linkedin' || isNew}
                    >
                      {generatingSocial === 'linkedin' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                      Générer
                    </Button>
                  </div>
                  {socialPosts.linkedin.fr && (
                    <div className="space-y-2">
                      <div className="relative">
                        <Textarea value={socialPosts.linkedin.fr} readOnly rows={5} />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={() => copyToClipboard(socialPosts.linkedin.fr)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                      {socialPosts.linkedin.en && (
                        <div className="relative">
                          <Label className="text-xs text-muted-foreground">Version anglaise</Label>
                          <Textarea value={socialPosts.linkedin.en} readOnly rows={4} className="mt-1" />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="absolute top-6 right-2"
                            onClick={() => copyToClipboard(socialPosts.linkedin.en)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Facebook */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Facebook className="h-5 w-5 text-blue-500" />
                      <span className="font-medium">Facebook</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => generateSocialPost('facebook', true)}
                      disabled={generatingSocial === 'facebook' || isNew}
                    >
                      {generatingSocial === 'facebook' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                      Générer
                    </Button>
                  </div>
                  {socialPosts.facebook.fr && (
                    <div className="relative">
                      <Textarea value={socialPosts.facebook.fr} readOnly rows={4} />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => copyToClipboard(socialPosts.facebook.fr)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Instagram */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Instagram className="h-5 w-5 text-pink-500" />
                      <span className="font-medium">Instagram</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => generateSocialPost('instagram', false)}
                      disabled={generatingSocial === 'instagram' || isNew}
                    >
                      {generatingSocial === 'instagram' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                      Générer
                    </Button>
                  </div>
                  {socialPosts.instagram.fr && (
                    <div className="relative">
                      <Textarea value={socialPosts.instagram.fr} readOnly rows={3} />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => copyToClipboard(socialPosts.instagram.fr)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Twitter/X */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Twitter className="h-5 w-5" />
                      <span className="font-medium">X (Twitter)</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => generateSocialPost('twitter', false)}
                      disabled={generatingSocial === 'twitter' || isNew}
                    >
                      {generatingSocial === 'twitter' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                      Générer
                    </Button>
                  </div>
                  {socialPosts.twitter.fr && (
                    <div className="relative">
                      <Textarea value={socialPosts.twitter.fr} readOnly rows={2} />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => copyToClipboard(socialPosts.twitter.fr)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* HTML Preview Dialog */}
      <Dialog open={previewHtml} onOpenChange={setPreviewHtml}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Prévisualisation de la page</DialogTitle>
          </DialogHeader>
          <div 
            className="border rounded-lg p-4 bg-white"
            dangerouslySetInnerHTML={{ __html: form.landing_page_html }}
          />
        </DialogContent>
      </Dialog>

      {/* Payment Link Dialog */}
      <Dialog open={!!paymentLinkDialog} onOpenChange={() => setPaymentLinkDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {paymentLinkDialog?.payment_method === 'stripe' ? (
                <><CreditCard className="h-5 w-5 text-purple-600" /> Lien de paiement Stripe</>
              ) : (
                <><Landmark className="h-5 w-5 text-blue-600" /> Informations de virement</>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {paymentLinkDialog && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Candidat:</span>
                  <span className="font-medium">{paymentLinkDialog.candidate_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email:</span>
                  <span>{paymentLinkDialog.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Montant:</span>
                  <span className="font-bold text-lg">{paymentLinkDialog.amount} {paymentLinkDialog.currency}</span>
                </div>
              </div>

              {paymentLinkDialog.payment_method === 'stripe' && paymentLinkDialog.stripe_payment_url && (
                <div className="space-y-3">
                  <Label>Lien de paiement</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={paymentLinkDialog.stripe_payment_url} 
                      readOnly 
                      className="font-mono text-xs"
                    />
                    <Button onClick={() => copyToClipboard(paymentLinkDialog.stripe_payment_url)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      className="flex-1" 
                      onClick={() => window.open(paymentLinkDialog.stripe_payment_url, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Ouvrir le lien
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => {
                        const subject = encodeURIComponent(`Paiement pour ${paymentLinkDialog.session_name}`);
                        const body = encodeURIComponent(`Bonjour ${paymentLinkDialog.candidate_name},\n\nVoici le lien pour procéder au paiement de votre inscription:\n\n${paymentLinkDialog.stripe_payment_url}\n\nCordialement`);
                        window.open(`mailto:${paymentLinkDialog.email}?subject=${subject}&body=${body}`);
                      }}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Envoyer par email
                    </Button>
                  </div>
                </div>
              )}

              {paymentLinkDialog.payment_method === 'bank_transfer' && paymentLinkDialog.bank_transfer_info && (
                <div className="space-y-3">
                  <Label>Informations bancaires</Label>
                  <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg space-y-2 font-mono text-sm">
                    <div className="flex justify-between">
                      <span>Banque:</span>
                      <span className="font-medium">{paymentLinkDialog.bank_transfer_info.bank_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Titulaire:</span>
                      <span className="font-medium">{paymentLinkDialog.bank_transfer_info.account_holder}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>IBAN:</span>
                      <span className="font-medium">{paymentLinkDialog.bank_transfer_info.iban}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>BIC:</span>
                      <span className="font-medium">{paymentLinkDialog.bank_transfer_info.bic}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2 mt-2">
                      <span>Montant:</span>
                      <span className="font-bold">{paymentLinkDialog.bank_transfer_info.amount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Référence:</span>
                      <span className="font-bold text-blue-600">{paymentLinkDialog.bank_transfer_info.reference}</span>
                    </div>
                  </div>
                  <Button 
                    className="w-full"
                    variant="outline"
                    onClick={() => {
                      const info = paymentLinkDialog.bank_transfer_info;
                      const subject = encodeURIComponent(`Paiement par virement - ${paymentLinkDialog.session_name}`);
                      const body = encodeURIComponent(`Bonjour ${paymentLinkDialog.candidate_name},\n\nVoici les informations pour procéder au paiement par virement:\n\nBanque: ${info.bank_name}\nTitulaire: ${info.account_holder}\nIBAN: ${info.iban}\nBIC: ${info.bic}\n\nMontant: ${info.amount}\nRéférence obligatoire: ${info.reference}\n\nCordialement`);
                      window.open(`mailto:${paymentLinkDialog.email}?subject=${subject}&body=${body}`);
                    }}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Envoyer les infos par email
                  </Button>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentLinkDialog(null)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmDialog} onOpenChange={() => setDeleteConfirmDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Confirmer la suppression
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Êtes-vous sûr de vouloir supprimer l'inscription de{' '}
              <strong>{deleteConfirmDialog?.full_name}</strong> ({deleteConfirmDialog?.email}) ?
            </p>
            <p className="text-sm text-red-500 mt-2">
              Cette action est irréversible.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirmDialog(null)}>
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => deleteRegistration(deleteConfirmDialog?.id)}
              disabled={deletingRegistration === deleteConfirmDialog?.id}
            >
              {deletingRegistration === deleteConfirmDialog?.id ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Payment Dialog */}
      <Dialog open={!!confirmPaymentDialog} onOpenChange={() => setConfirmPaymentDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              Confirmer le paiement
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Confirmez-vous avoir reçu le paiement de{' '}
              <strong>{confirmPaymentDialog?.full_name}</strong> ?
            </p>
            <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex justify-between items-center">
                <span className="text-sm text-green-700">Montant:</span>
                <span className="text-lg font-bold text-green-700">
                  {confirmPaymentDialog?.amount} {confirmPaymentDialog?.selected_currency}
                </span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-sm text-green-700">Mode:</span>
                <span className="text-sm font-medium text-green-700">
                  {confirmPaymentDialog?.payment_method === 'bank_transfer' ? 'Virement bancaire' : 'Carte'}
                </span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              Un email de confirmation sera envoyé automatiquement au candidat.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmPaymentDialog(null)}>
              Annuler
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => confirmPayment(confirmPaymentDialog?.id)}
              disabled={confirmingPayment === confirmPaymentDialog?.id}
            >
              {confirmingPayment === confirmPaymentDialog?.id ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Confirmer le paiement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
